<script lang="ts">
/**
 * Orchestrates slide ↔ reader: `StudyPresentationShell` receives `layout` from `mode` and shows a
 * Slides/Reader toggle only when `?mode=dev`. Shared chrome lives in `StudyPresentationShell.svelte`.
 */
import { building } from '$app/environment'
import { page } from '$app/state'
import StudyPresentationShell from '$lib/components/study/StudyPresentationShell.svelte'
import type { StudyMeta } from '$lib/utils/report/types'
import { tick, type Snippet } from 'svelte'

interface Props {
  /** Browser tab title (passed through to the shell). */
  title: string
  /** Slide markup; same snippet API as the shell. */
  children: Snippet<
    [
      {
        goTo: (idx: number) => void
        activeSlideId: string
        slideActive: (slideId: string) => boolean
      },
    ]
  >
  /**
   * `slide` = one slide at a time; `reader` = stacked scroll. Use `bind:mode` to control from a parent.
   * @default 'slide'
   */
  mode?: 'slide' | 'reader'
  /**
   * Pill control to switch modes. Only shown when `?mode=dev` (and when true).
   * @default true
   */
  showModeToggle?: boolean
  /** When provided, renders a citation chip in the bottom-right corner of the presentation. */
  meta?: StudyMeta
}

let {
  title,
  children: childrenSnippet,
  mode = $bindable<'slide' | 'reader'>('slide'),
  showModeToggle = true,
  meta,
}: Props = $props()

const devMode = $derived(!building && page.url.searchParams.get('mode') === 'dev')

const showToggle = $derived(showModeToggle && devMode)

/** HTML export: switch to reader so all charts can mount, then restore slide mode after download. */
async function requestReaderModeForExport(): Promise<void> {
  mode = 'reader'
  await tick()
  await tick()
  await new Promise<void>((r) => setTimeout(r, 450))
}

async function restoreSlideModeAfterExport(): Promise<void> {
  mode = 'slide'
  await tick()
  await tick()
  await new Promise<void>((r) => setTimeout(r, 250))
}
</script>

<StudyPresentationShell
  layout={mode}
  {title}
  {meta}
  {requestReaderModeForExport}
  {restoreSlideModeAfterExport}
>
  {#snippet children({ goTo, activeSlideId, slideActive })}
    {@render childrenSnippet({ goTo, activeSlideId, slideActive })}
  {/snippet}
</StudyPresentationShell>

{#if showToggle}
  <div class="study-presentation-mode-toggle" role="group" aria-label="Presentation layout">
    <button
      type="button"
      class="study-presentation-mode-toggle__btn"
      class:study-presentation-mode-toggle__btn--active={mode === 'slide'}
      aria-pressed={mode === 'slide'}
      onclick={() => (mode = 'slide')}
    >
      Slides
    </button>
    <button
      type="button"
      class="study-presentation-mode-toggle__btn"
      class:study-presentation-mode-toggle__btn--active={mode === 'reader'}
      aria-pressed={mode === 'reader'}
      onclick={() => (mode = 'reader')}
    >
      Reader
    </button>
  </div>
{/if}

<style>
.study-presentation-mode-toggle {
  position: fixed;
  top: calc(0.65rem + env(safe-area-inset-top, 0px));
  right: calc(0.65rem + env(safe-area-inset-right, 0px));
  z-index: 4000;
  display: flex;
  gap: 0;
  padding: 3px;
  border-radius: 999px;
  background: rgba(3, 11, 10, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.35);
  pointer-events: auto;
}
:global(html:not(.dark)) .study-presentation-mode-toggle {
  background: rgba(255, 255, 255, 0.88);
  border-color: rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}
.study-presentation-mode-toggle__btn {
  appearance: none;
  border: none;
  margin: 0;
  padding: 0.38rem 0.72rem;
  font-family: 'Space Grotesk', system-ui, sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  border-radius: 999px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.55);
  background: transparent;
  transition:
    color 0.2s ease,
    background 0.2s ease;
}
:global(html:not(.dark)) .study-presentation-mode-toggle__btn {
  color: rgba(10, 18, 17, 0.45);
}
.study-presentation-mode-toggle__btn:hover {
  color: rgba(255, 255, 255, 0.88);
}
:global(html:not(.dark)) .study-presentation-mode-toggle__btn:hover {
  color: rgba(10, 18, 17, 0.72);
}
.study-presentation-mode-toggle__btn--active {
  color: #030b0a !important;
  background: linear-gradient(135deg, #22d4dc, #11a0a5) !important;
  box-shadow: 0 0 12px rgba(34, 212, 220, 0.35);
}
:global(html:not(.dark)) .study-presentation-mode-toggle__btn--active {
  color: #fff !important;
}
.study-presentation-mode-toggle__btn:focus-visible {
  outline: 2px solid rgba(34, 212, 220, 0.85);
  outline-offset: 2px;
}
@media (max-width: 520px) {
  .study-presentation-mode-toggle {
    top: calc(0.45rem + env(safe-area-inset-top, 0px));
    right: calc(0.45rem + env(safe-area-inset-right, 0px));
  }
  .study-presentation-mode-toggle__btn {
    padding: 0.32rem 0.55rem;
    font-size: 0.65rem;
  }
}
</style>
